// assets/js/gsap.js

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/**
 * Get elements from scope
 * Includes the scope itself if it matches the selector.
 */
const getElements = (root, selector) => {
  const elements = [];

  if (root instanceof Element && root.matches(selector)) {
    elements.push(root);
  }

  elements.push(...root.querySelectorAll(selector));

  return elements;
};

/**
 * Fade animations
 * Can be called again after AJAX/load-more/filter updates.
 */
const initFadeAnimations = (scope = document) => {
  const root = scope instanceof Element || scope instanceof Document ? scope : document;

  // Fade Up groups
  gsap.utils.toArray(getElements(root, ".fade-group")).forEach((group) => {
    const items = group.querySelectorAll(".fade-up:not(.gsap-ready)");

    if (!items.length) return;

    items.forEach((item) => item.classList.add("gsap-ready"));

    gsap.fromTo(
      items,
      {
        y: 40,
        autoAlpha: 0,
      },
      {
        y: 0,
        autoAlpha: 1,
        duration: 1,
        ease: "power3.out",
        stagger: 0.2,
        scrollTrigger: {
          trigger: group,
          start: "top 85%",
          once: true,
        },
      }
    );
  });

  // Fade Up single
  gsap.utils.toArray(getElements(root, ".fade-up:not(.gsap-ready)")).forEach((el) => {
    if (el.closest(".fade-group")) return;

    el.classList.add("gsap-ready");

    gsap.fromTo(
      el,
      {
        y: 40,
        autoAlpha: 0,
      },
      {
        y: 0,
        autoAlpha: 1,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          once: true,
        },
      }
    );
  });

  // Fade Left
  gsap.utils.toArray(getElements(root, ".fade-left:not(.gsap-ready)")).forEach((el) => {
    el.classList.add("gsap-ready");

    gsap.fromTo(
      el,
      {
        x: -40,
        autoAlpha: 0,
      },
      {
        x: 0,
        autoAlpha: 1,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          once: true,
        },
      }
    );
  });

  // Fade Right
  gsap.utils.toArray(getElements(root, ".fade-right:not(.gsap-ready)")).forEach((el) => {
    el.classList.add("gsap-ready");

    gsap.fromTo(
      el,
      {
        x: 40,
        autoAlpha: 0,
      },
      {
        x: 0,
        autoAlpha: 1,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          once: true,
        },
      }
    );
  });

  ScrollTrigger.refresh();
};

/**
 * Share buttons
 */
const initShareButtons = (scope = document) => {
  const root = scope instanceof Element || scope instanceof Document ? scope : document;

  gsap.utils.toArray(getElements(root, ".share-lnk__items:not(.share-ready)")).forEach((shareItem) => {
    shareItem.classList.add("share-ready");

    const btn = shareItem.querySelector(".btn-share");
    const buttonsWrap = shareItem.querySelector(".share-buttons");
    const buttons = gsap.utils.toArray(buttonsWrap?.querySelectorAll("a, button"));

    if (!btn || !buttonsWrap || !buttons.length) return;

    let timeline = null;
    // const gap = window.innerWidth < 991 ? 2 : 8;
    const getGap = () => (window.innerWidth < 768 ? 2 : 8);

    const reset = () => {
      if (timeline) {
        timeline.kill();
        timeline = null;
      }

      gsap.set(buttonsWrap, {
        pointerEvents: "none",
      });

      gsap.set(buttons, {
        clearProps: "x,transform",
        x: 0,
        scale: 0.4,
        opacity: 0,
        visibility: "hidden",
      });
    };

    const createTimeline = () => {
    const btnRect = btn.getBoundingClientRect();
    const wrapRect = buttonsWrap.getBoundingClientRect();

    const btnWidth = btnRect.width;
    const buttonSize = btnRect.height;

    // position of .btn-share relative to .share-buttons
    const startX = btnRect.left - wrapRect.left;

    console.log("btn start X:", btnRect.left);
    console.log("share-buttons start X relative to btn:", startX);

    timeline = gsap.timeline({
        paused: true,
        defaults: {
        duration: 0.45,
        ease: "power3.out",
        },
    });

    buttons.forEach((button, index) => {

        const gap = getGap();

        const x = startX + btnWidth + gap + index * (buttonSize + gap);

        // const x = startX + btnWidth + gap + index * (buttonSize + gap);

        timeline.to(
        button,
        {
            x,
            scale: 1,
            opacity: 1,
            visibility: "visible",
        },
        index * 0.04
        );
    });
    };

    const open = () => {
      reset();
      createTimeline();

      btn.classList.add("is-active");

      gsap.set(buttonsWrap, {
        pointerEvents: "auto",
      });

      timeline.play(0);
    };

    const close = () => {
      btn.classList.remove("is-active");

      gsap.set(buttonsWrap, {
        pointerEvents: "none",
      });

      timeline?.reverse();
    };

    btn.addEventListener("click", (e) => {
      e.stopPropagation();

      if (btn.classList.contains("is-active")) {
        close();
      } else {
        open();
      }
    });

    document.addEventListener("click", (e) => {
      if (!shareItem.contains(e.target)) {
        close();
      }
    });


    // log starting position
    const wrapRect = buttonsWrap.getBoundingClientRect();

    console.log("X:", wrapRect.left);
    console.log("Y:", wrapRect.top);


    reset();
  });
};

/**
 * Main init
 */
const initGsapAnimations = (scope = document) => {
  initFadeAnimations(scope);
  initShareButtons(scope);
};

// Make available for AJAX / filters / load more
window.initFadeAnimations = initFadeAnimations;
window.initShareButtons = initShareButtons;
window.initGsapAnimations = initGsapAnimations;

window.addEventListener("load", () => {
  initGsapAnimations();
});